// Web-only data of the signed-in user (/app/v1/me): profile details and UI preferences.
import { z } from "zod";
import { Identity } from "./identity.js";

export const Address = z.object({
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable(),
  postalCode: z.string().trim().min(1).max(20),
  city: z.string().trim().min(1).max(100),
  country: z.string().regex(/^[A-Z]{2}$/), // ISO 3166-1 alpha-2
});
export type Address = z.infer<typeof Address>;

/** Profile details the token does not carry; name and email stay in Keycloak. */
export const Profile = z.object({
  jobTitle: z.string().trim().max(120).nullable(),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ().-]{6,25}$/)
    .nullable(),
  address: Address.nullable(),
});
export type Profile = z.infer<typeof Profile>;

export const EMPTY_PROFILE: Profile = { jobTitle: null, phone: null, address: null };

/** A spec marked as a favorite: its reference within a workspace. */
export const Favorite = z.object({ workspace: z.string().min(1), ref: z.string().min(1).max(300) });
export type Favorite = z.infer<typeof Favorite>;

export const ThemePreference = z.enum(["system", "light", "dark"]);
export type ThemePreference = z.infer<typeof ThemePreference>;

export const LanguagePreference = z.enum(["en", "fr"]);
export type LanguagePreference = z.infer<typeof LanguagePreference>;

export const Density = z.enum(["comfortable", "compact"]);
export type Density = z.infer<typeof Density>;

/** Pure UI preferences; one JSON value per user, validated on the way in and out. */
export const Preferences = z.object({
  theme: ThemePreference,
  language: LanguagePreference,
  density: Density,
  sidebarGroups: z.record(z.string(), z.boolean()),
  favorites: z.array(Favorite).max(200),
  /** The workspace the Studio opens on; null until one is chosen. */
  lastWorkspace: z.string().min(1).max(63).nullable(),
});
export type Preferences = z.infer<typeof Preferences>;

/** A partial update; each field given replaces the saved one. */
export const PreferencesPatch = Preferences.partial();
export type PreferencesPatch = z.infer<typeof PreferencesPatch>;

export const DEFAULT_PREFERENCES: Preferences = {
  theme: "system",
  language: "en",
  density: "comfortable",
  sidebarGroups: {},
  favorites: [],
  lastWorkspace: null,
};

/** Everything the web side keeps about a user, as downloaded by GET /app/v1/me/export. */
export const MeExport = z.object({
  identity: Identity,
  firstSeenAt: z.string(),
  profile: Profile,
  preferences: Preferences,
});
export type MeExport = z.infer<typeof MeExport>;
